import React from 'react';
import Card, {
  BORDER_KIND,
  SHADOW_KIND,
} from '@bitComponents/unifiedComponents/Card';
import CustomLink from '@bitComponents/unifiedComponents/CustomLink';
import TextInfo, {
  TEXT_SIZE,
  TEXT_COLOR,
  TEXT_FONT_WEIGHT,
} from '@gcloud-npm/components.text';
import Button, {
  BUTTON_HEIGHT,
  BUTTON_WIDTH,
  BUTTON_COLOR,
} from '@bitComponents/unifiedComponents/Button';
import SvgIconLoader, {
  ICONS_ID,
  COLOR as ICON_COLOR,
  SVG_ICON_KIND,
} from '@bitComponents/unifiedIcons/SvgIconLoader';
import PropTypes from 'prop-types';
import './BasicModalContent.scss';
import { LIGHT_BOX_CONTENT } from '@bitComponents/utils/tagging';
import { BORDER_COLOR } from '../Card';

export const COMPONENT_NAME = 'BasicModalContent';

const BasicModalContent = ({
  className,
  title,
  children,
  hideAdviceIcon,
  hideCloseIcon,
  closeIconAction,
  taggingInfo,
  mainButtonProps,
  secondaryButtonProps,
}) => {
  const handleXIconOnClick = () => {
    closeIconAction();
  };
  const finalTaggingInfo = {
    ...taggingInfo,
    componentVariation: taggingInfo.componentVariation || 'all',
    componentProduct: taggingInfo.componentProduct || '',
    componentName: taggingInfo.componentName || COMPONENT_NAME,
  };
  return (
    <div className={`basicModalContent ${className}`}>
      <Card
        border={BORDER_KIND.FULL_ROUNDED}
        borderColor={BORDER_COLOR.TRANSPARENT}
        shadow={SHADOW_KIND.SHADOW1}
        isClickable={false}
      >
        <div className="basicModalContent__container">
          {!hideCloseIcon && (
            <div className="basicModalContent__closeIcon">
              <CustomLink
                onClick={handleXIconOnClick}
                taggingInfo={{
                  ...finalTaggingInfo,
                  componentContent: `basic-modal-${LIGHT_BOX_CONTENT.CLICK_CLOSE}`,
                }}
              >
                <SvgIconLoader id={ICONS_ID.Cerrar} color={ICON_COLOR.BLUE_A} />
              </CustomLink>
            </div>
          )}
          {!hideAdviceIcon && (
            <SvgIconLoader
              id={ICONS_ID.Informacion}
              kind={SVG_ICON_KIND.LIGHT}
              color={ICON_COLOR.BLUE_A}
              size={64}
              className="basicModalContent__adviceIcon"
            />
          )}

          {title && (
            <TextInfo
              text={title}
              fontType={TEXT_FONT_WEIGHT.BOLD}
              size={TEXT_SIZE.TITLE4}
              color={TEXT_COLOR.BLUE_DARK}
            />
          )}
          {children && (
            <div className="basicModalContent__children">{children}</div>
          )}
          {mainButtonProps && (
            <div className="basicModalContent__buttonsContainer">
              <Button
                color={BUTTON_COLOR.BLUE}
                buttonType="button"
                onClick={mainButtonProps.onClick}
                label={mainButtonProps.label}
                taggingInfo={{
                  ...finalTaggingInfo,
                  componentContent: `basic-modal-main-button-${mainButtonProps.label}`,
                }}
                size={{
                  w: BUTTON_WIDTH.FULL,
                  h: BUTTON_HEIGHT.NORMAL,
                }}
                desktopProps={{
                  size: {
                    w: BUTTON_WIDTH.MEDIUM,
                    h: BUTTON_HEIGHT.NORMAL,
                  },
                }}
                operation={mainButtonProps.operation}
              />
              {secondaryButtonProps && (
                <Button
                  color={BUTTON_COLOR.BLUE_REVERSE}
                  buttonType="button"
                  onClick={secondaryButtonProps.onClick}
                  label={secondaryButtonProps.label}
                  taggingInfo={{
                    ...finalTaggingInfo,
                    componentContent: `basic-modal-secondary-button-${secondaryButtonProps.label}`,
                  }}
                  size={{
                    w: BUTTON_WIDTH.FULL,
                    h: BUTTON_HEIGHT.NORMAL,
                  }}
                  desktopProps={{
                    size: {
                      w: BUTTON_WIDTH.MEDIUM,
                      h: BUTTON_HEIGHT.NORMAL,
                    },
                  }}
                  operation={secondaryButtonProps.operation}
                  className="basicModalContent__buttonsContainer--secondary"
                />
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

BasicModalContent.propTypes = {
  /**
   * {
   *  "info": "Titulo de modal",
   *  "kind": "both",
   *  "beautifulName": "Titulo"
   * }
   */
  title: PropTypes.string.isRequired,
  /**
   * {
   *  "info": "Define la clase CSS custom que se aplicará al componente",
   *  "kind": "both",
   *  "beautifulName": "Estilos custom"
   * }
   */
  className: PropTypes.string.isRequired,
  /**
   * {
   *  "info": "Define el children del componente",
   *  "kind": "playground",
   *  "beautifulName": "Children"
   * }
   */
  children: PropTypes.node.isRequired,
  /**
   * {
   *  "info": "Permite ocultar el icono de aviso",
   *  "kind": "playground",
   *  "beautifulName": "Ocultar aviso"
   * }
   */
  hideAdviceIcon: PropTypes.bool,
  /**
   * {
   *  "info": "Permite ocultar el icono de cerrar ",
   *  "kind": "playground",
   *  "beautifulName": "Ocultar icono"
   * }
   */
  hideCloseIcon: PropTypes.bool,
  /**
   * {
   *  "info": "Accion ejecutada al pulsar el icono de cerrar",
   *  "kind": "playground",
   *  "beautifulName": "Ocultar icono"
   * }
   */
  closeIconAction: PropTypes.func,
  /**
   * {
   *  "info": "Permite contribuir la infomación que será enviada a analítica tras la interacción (objeto para el taggeado)",
   *  "kind": "both",
   *  "beautifulName": "Propiedades tagueo"
   * }
   */
  taggingInfo: PropTypes.shape({
    event: PropTypes.string,
    eventType: PropTypes.string,
    componentName: PropTypes.string,
    componentVariation: PropTypes.string.isRequired,
    componentContent: PropTypes.string.isRequired,
    componentProduct: PropTypes.string.isRequired,
    componentPerso: PropTypes.any,
    debug: PropTypes.bool,
    customizerId: PropTypes.string,
  }),
  /**
   * {
   *  "info": "Propiedades para el boton principal",
   *  "kind": "playground",
   *  "beautifulName": "Boton principal"
   * }
   */
  mainButtonProps: PropTypes.shape({
    onClick: PropTypes.func.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    operation: PropTypes.string.isRequired,
  }),
  /**
   * {
   *  "info": "Propiedades para el boton secundario",
   *  "kind": "playground",
   *  "beautifulName": "Boton secundario"
   * }
   */
  secondaryButtonProps: PropTypes.shape({
    onClick: PropTypes.func.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    operation: PropTypes.string.isRequired,
  }),
};

BasicModalContent.defaultProps = {
  hideAdviceIcon: false,
  hideCloseIcon: false,
  closeIconAction: () => {},
  taggingInfo: {},
  mainButtonProps: null,
  secondaryButtonProps: null,
  linksProps: null,
};

export default BasicModalContent;
